import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { UserInfo, UserService } from 'src/app/services/user.service';
import { SupportModalComponent } from '../support-modal/support-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { LogoService } from '../../services/logo.service';
import { ThemeService } from '../../core/services/theme.service';
import { UserAvatarService } from '../navbar/services/user-avatar.service';
import { AssetUpdaterService } from '../../core/services/asset-updater.service';
import { environment } from '../../../environments/environment';
import { WorkOrdersService } from 'src/app/pages/work-orders-protocols/services/work-orders.service';
import { ModalDraftOrdersComponent } from 'src/app/pages/work-orders-protocols/modals/modal-draft-orders/modal-draft-orders.component';
import { ModalSelectProtocolTypeComponent } from 'src/app/pages/work-orders-protocols/modals/modal-select-protocol-type/modal-select-protocol-type.component';
import { LoaderService } from '../../core/services/loader.service';
import { ROUTE_PATHS } from '../../core/constants/route.constants';
import { RefreshService } from '../../core/services/refresh.service';

const DEFAULT_AVATAR = 'assets/icons/navbar/nav-user-icon.svg';
const DEFAULT_CHEVRON = 'assets/icons/chevron-left-dark.svg';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
})
export class TopbarComponent implements OnInit, AfterViewInit, OnDestroy {
  // View References
  @ViewChild('userAvatarImg', { static: false }) private userAvatarRef?: ElementRef<HTMLImageElement>;
  @ViewChild('chevronImg', { static: false }) private chevronImgRef?: ElementRef<HTMLImageElement>;

  // UI State
  isRotated = false;
  isDropdownOpen = false;
  isWorkshopSettingsOpen = false;
  isGeneralSettingsOpen = false;
  isWarehousesOpen = false;

  // User Data
  userName = 'Test User';
  userEmail = 'test@gmail.com';
  userImg = DEFAULT_AVATAR;

  // Computed Properties
  get showTapToPay(): boolean {
    return !environment.production;
  }

  // Chevron Image Management
  private _chevronImg = DEFAULT_CHEVRON;
  get chevronImg(): string {
    return this._chevronImg;
  }
  set chevronImg(value: string) {
    if (!this.isValidImageUrl(value)) {
      if (!environment.production) {
        console.warn('[Topbar] Invalid chevron image:', value);
      }
      return;
    }
    const normalized = value.trim();
    if (normalized !== this._chevronImg) {
      this._chevronImg = normalized;
      this.updateChevronImage(normalized);
    }
  }

  // Subscriptions Management
  private subscriptions: Subscription[] = [];
  private customEventListener?: EventListenerOrEventListenerObject;
  readonly logoutPath = ROUTE_PATHS.LOGOUT;

  constructor(
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly dialog: MatDialog,
    private readonly logoService: LogoService,
    private readonly themeService: ThemeService,
    private readonly userAvatarService: UserAvatarService,
    private readonly assetUpdaterService: AssetUpdaterService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly workOrdersService: WorkOrdersService,
    private readonly loaderService: LoaderService,
    private readonly refreshService: RefreshService
  ) { }

  ngOnInit(): void {
    this.initializeDefaults();
    this.subscribeToUserInfo();
    this.setupAssetSubscriptions();
  }

  ngAfterViewInit(): void {
    this.updateImageElements();
    this.registerGlobalHelpers();
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
    if (this.customEventListener) {
      document.removeEventListener('flatThemeAssetsUpdated', this.customEventListener);
    }
  }

  // ==================== Initialization ====================

  private initializeDefaults(): void {
    this.chevronImg = DEFAULT_CHEVRON;
    this.userImg = DEFAULT_AVATAR;
  }

  private subscribeToUserInfo(): void {
    const subscription = this.userService.getUserInfo().subscribe({
      next: (user) => this.handleUserInfo(user),
      error: (error) => console.error('[Topbar] Failed to fetch user info:', error),
    });
    this.subscriptions.push(subscription);
  }

  private handleUserInfo(user: UserInfo | null): void {
    if (user) {
      this.userName = user.name ?? this.userName;
      this.userEmail = user.email ?? this.userEmail;
      this.loadUserAvatar(user.img);
    } else {
      this.userName = 'Guest User';
      this.userEmail = 'No Email Provided';
      this.loadUserAvatar();
    }
    this.cdRef.markForCheck();
  }

  private loadUserAvatar(avatarUrl?: string): void {
    const avatarSubscription = this.userAvatarService.getFinalAvatarUrl(avatarUrl).subscribe({
      next: (url) => {
        if (url) this.userImg = url;
      },
      error: (error) => {
        if (!environment.production) {
          console.warn('[Topbar] Avatar fetch error:', error);
        }
      },
    });
    this.subscriptions.push(avatarSubscription);
  }

  private setupAssetSubscriptions(): void {
    this.subscribeToAvatarUpdates();
    this.subscribeToChevronUpdates();
    this.setupCustomEventListener();
  }

  private subscribeToAvatarUpdates(): void {
    const subscription = this.assetUpdaterService.userAvatarUrl$.subscribe((avatarUrl) => {
      if (avatarUrl && this.isValidImageUrl(avatarUrl)) {
        this.userImg = avatarUrl;
        this.updateAvatarImage(avatarUrl);
      }
    });
    this.subscriptions.push(subscription);
  }

  private subscribeToChevronUpdates(): void {
    const subscription = this.assetUpdaterService.chevronImageUrl$.subscribe((chevronUrl) => {
      if (chevronUrl && this.isValidImageUrl(chevronUrl) && chevronUrl !== this.chevronImg) {
        this.chevronImg = chevronUrl;
      }
    });
    this.subscriptions.push(subscription);
  }

  private setupCustomEventListener(): void {
    this.customEventListener = (event: Event) => this.handleCustomEvent(event);
    document.addEventListener('flatThemeAssetsUpdated', this.customEventListener);
  }

  private handleCustomEvent(event: Event): void {
    try {
      const { detail } = event as CustomEvent;
      const { userAvatarUrl, chevronImageUrl } = detail || {};

      if (this.isValidImageUrl(userAvatarUrl)) {
        this.userImg = userAvatarUrl;
        this.updateAvatarImage(userAvatarUrl);
      }

      if (this.isValidImageUrl(chevronImageUrl)) {
        this.chevronImg = chevronImageUrl;
      }
    } catch (error) {
      if (!environment.production) {
        console.warn('[Topbar] Custom event handling error:', error);
      }
    }
  }

  private updateImageElements(): void {
    this.updateChevronImage(this.chevronImg);
    this.updateAvatarImage(this.userImg);
  }

  private updateChevronImage(url: string): void {
    if (this.chevronImgRef?.nativeElement) {
      this.chevronImgRef.nativeElement.src = url;
    }
  }

  private updateAvatarImage(url: string): void {
    if (this.userAvatarRef?.nativeElement) {
      this.userAvatarRef.nativeElement.src = url;
    }
  }

  private registerGlobalHelpers(): void {
    if (typeof window !== 'undefined') {
      (window as any).forceResetChevron = () => this.forceResetChevron();
    }
  }

  private cleanupSubscriptions(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    if (this.customEventListener) {
      document.removeEventListener('flatThemeAssetsUpdated', this.customEventListener);
    }
  }

  // ==================== Validation ====================

  private isValidImageUrl(url?: string | null): boolean {
    if (!url || typeof url !== 'string') return false;

    const trimmedUrl = url.trim();
    if (!trimmedUrl) return false;

    // Security check
    const dangerousProtocols = ['javascript:', 'data:text/html', 'about:blank'];
    if (dangerousProtocols.some(protocol => trimmedUrl.startsWith(protocol))) {
      return false;
    }

    // Valid URL patterns
    const validPatterns = [
      /^https?:\/\//,
      /^data:image\//,
      /^\//,
      /^assets\//,
      /\.(svg|png|jpg|jpeg|gif|webp|ico|bmp)$/i
    ];

    return validPatterns.some(pattern => pattern.test(trimmedUrl));
  }

  // ==================== UI Actions ====================

  toggleDropdown(): void {
    this.isRotated = !this.isRotated;
    this.isDropdownOpen = !this.isDropdownOpen;

    if (!this.isDropdownOpen) {
      this.closeAllSubmenus();
    }
  }

  toggleWorkshopSettings(event: MouseEvent): void {
    event.stopPropagation();
    this.isWorkshopSettingsOpen = !this.isWorkshopSettingsOpen;
  }

  toggleGeneralSettings(event: MouseEvent): void {
    event.stopPropagation();
    this.isGeneralSettingsOpen = !this.isGeneralSettingsOpen;
  }

  toggleWarehouses(event: MouseEvent): void {
    event.stopPropagation();
    this.isWarehousesOpen = !this.isWarehousesOpen;
  }

  private closeAllSubmenus(): void {
    this.isWorkshopSettingsOpen = false;
    this.isGeneralSettingsOpen = false;
    this.isWarehousesOpen = false;
  }

  // ==================== Navigation ====================

  navigateToAddWarehouse(): void {
    this.closeDropdown();
    this.router.navigate(['/storage'], { queryParams: { openModal: 'true' } });
  }

  navigateToKanban(): void {
    this.router.navigate(['/kanban']);
  }

  onLogoClick(): void {
    void this.refreshService.refreshAll();
  }

  // ==================== Modals ====================

  openModal(event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open(SupportModalComponent, {
      width: '80%',
      maxWidth: '800px',
      panelClass: 'modal-dialog-centered',
    });
  }

  // ==================== Error Handling ====================

  forceResetChevron(): void {
    this.chevronImg = DEFAULT_CHEVRON;
    this.updateChevronImage(DEFAULT_CHEVRON);
  }

  onChevronImageError(event: Event): void {
    if (this.chevronImg !== DEFAULT_CHEVRON) {
      this.chevronImg = DEFAULT_CHEVRON;
      (event.target as HTMLImageElement).src = DEFAULT_CHEVRON;
    }
  }

  onUserImageError(event: Event): void {
    if (this.userImg !== DEFAULT_AVATAR) {
      this.userImg = DEFAULT_AVATAR;
      (event.target as HTMLImageElement).src = DEFAULT_AVATAR;
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.isRotated = false;
    this.closeAllSubmenus();
  }

  // ==================== Event Listeners ====================

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Close dropdown if clicked outside
    if (!target.closest('.profile-card') && !target.closest('.dropdown')) {
      this.closeDropdown();
    }
  }

  // ==================== Utilities ====================

  checkSamePath(subSectionPath: string): boolean {
    const currentPath = this.router.url || '';
    const segments = currentPath.split('/');
    const lastSegment = segments[segments.length - 1] || '';
    return lastSegment === subSectionPath;
  }

  redirectHelp(): void {
    const helpUrl = 'https://cafler.atlassian.net/wiki/external/ZjI5MWFlZDM2NGIyNDlhODkyNTM0NDFiODE0NmQ2MjA';
    window.open(helpUrl, '_blank', 'noopener,noreferrer');
  }

  // ==================== Work Order Workflow ====================

  async navigateToNewWorkOrder(): Promise<void> {
    try {
      const protocolType = await this.selectProtocolType();
      if (!protocolType) return;

      if (protocolType === 'quick') {
        await this.router.navigate(['/work-orders/new/quick']);
        return;
      }

      await this.handleProtocolWorkflow();
    } catch (error) {
      this.handleWorkOrderError(error);
    }
  }

  private async selectProtocolType(): Promise<string | null> {
    const dialog = this.dialog.open(ModalSelectProtocolTypeComponent, {
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'modal-dialog-centered',
      disableClose: false
    });

    const result = await firstValueFrom(dialog.afterClosed());
    return result?.type || null;
  }

  private async handleProtocolWorkflow(): Promise<void> {
    this.loaderService.show();

    try {
      const drafts = await firstValueFrom(
        this.workOrdersService.getDraftProtocolOrders()
      );

      this.loaderService.hide();

      if (drafts?.length > 0) {
        await this.handleExistingDrafts(drafts);
      } else {
        await this.router.navigate(['/work-orders/new/entry/custody']);
      }
    } catch (error) {
      this.loaderService.hide();
      throw error;
    }
  }

  private async handleExistingDrafts(drafts: any[]): Promise<void> {
    const dialog = this.dialog.open(ModalDraftOrdersComponent, {
      width: '700px',
      maxWidth: '90vw',
      panelClass: 'modal-dialog-centered',
      data: { drafts }
    });

    const result = await firstValueFrom(dialog.afterClosed());

    if (result?.action === 'continue' && result.draftId) {
      await this.router.navigate(['/work-orders/draft', result.draftId]);
    } else if (result?.action === 'new') {
      await this.router.navigate(['/work-orders/new/entry/custody']);
    }
  }

  private handleWorkOrderError(error: unknown): void {
    this.loaderService.hide();
    console.error('[Topbar] Work order workflow error:', error);
  }
}
